import { useState, useRef, useEffect, useCallback } from "react";
import MessageList from "./MessageList";
import MessageInput from "./MessageInput";
import ToolCallIndicator, { ToolCall } from "./ToolCallIndicator";
import ConfirmationModal, { ConfirmationRequest } from "./ConfirmationModal";
import { AlertCircle } from "lucide-react";
import AuthService from "../services/authService";
import { useTenant } from '../contexts/TenantContext';
import { useProject } from '../contexts/ProjectContext';
import "./ChatContainer.css";
import "./ToolCallIndicator.css";
import "./ConfirmationModal.css";

interface ThreadContext {
  tenantId?: string;
  tenantName?: string;
  projectUuid?: string;
  projectName?: string;
}

interface ChatContainerProps {
  threadId: string;
  onThreadCreated: (threadId: string) => void;
  threadTitle?: string;
  onUpdateThreadTitle?: (threadId: string, title: string, context?: ThreadContext) => void;
}

interface Message {
  id: string;
  content: string;
  role: "user" | "assistant" | "system";
  timestamp: Date;
  chartData?: any; // For chart data
  tableData?: any; // For table data
  failureReportData?: any; // For failure report data
  eventId?: number; // For feedback tracking
}

function ChatContainer({
  threadId,
  onThreadCreated,
  threadTitle: _threadTitle,
  onUpdateThreadTitle,
}: ChatContainerProps) {
  const { currentTenant, isSingleTenant } = useTenant();
  const { currentProject } = useProject();
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamedContent, setStreamedContent] = useState("");
  const [waitingForHumanAssistance, setWaitingForHumanAssistance] =
    useState(false);
  const [, setHumanAssistanceQuery] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);

  // Phase 4: Tool call transparency and confirmation
  const [activeToolCalls, setActiveToolCalls] = useState<ToolCall[]>([]);
  const [pendingConfirmation, setPendingConfirmation] = useState<ConfirmationRequest | null>(null);

  // Track EventSource for cancellation
  const eventSourceRef = useRef<EventSource | null>(null);

  // Listen for tenant/project context changes, add system messages, AND update thread context
  useEffect(() => {
    const handleTenantChange = (event: CustomEvent) => {
      const { current, tenantId, projectName } = event.detail;

      // Use project name from event (already read from localStorage by TenantContext)
      const projectInfo = projectName ? ` / ${projectName}` : ' / All Projects';

      // Only add system message if conversation has started (has non-system messages)
      // This keeps welcome screen visible on new chats
      setMessages((prev) => {
        const hasConversation = prev.some(m => m.role !== 'system');
        if (!hasConversation) return prev;

        const systemMessage: Message = {
          id: `system-${Date.now()}`,
          content: `Switched to ${current}${projectInfo}`,
          role: "system",
          timestamp: new Date(),
        };
        return [...prev, systemMessage];
      });

      // Update thread context with new tenant (keep project null since it will change)
      if (threadId && onUpdateThreadTitle) {
        // Get current title from localStorage to preserve it
        const savedThreads = localStorage.getItem('chatThreads');
        if (savedThreads) {
          const threads = JSON.parse(savedThreads);
          const currentThread = threads.find((t: any) => t.id === threadId);
          const currentTitle = currentThread?.title || 'New Chat';

          onUpdateThreadTitle(threadId, currentTitle, {
            tenantId: tenantId,
            tenantName: current,
            projectUuid: undefined, // Clear project on tenant change
            projectName: undefined,
          });
        }
      }
    };

    const handleProjectChange = (event: CustomEvent) => {
      const { current, projectUuid } = event.detail;

      // Only add system message if conversation has started (has non-system messages)
      // This keeps welcome screen visible on new chats
      setMessages((prev) => {
        const hasConversation = prev.some(m => m.role !== 'system');
        if (!hasConversation) return prev;

        const systemMessage: Message = {
          id: `system-${Date.now()}`,
          content: `Project changed to ${current}`,
          role: "system",
          timestamp: new Date(),
        };
        return [...prev, systemMessage];
      });

      // Update thread context with new project (preserve tenant)
      if (threadId && onUpdateThreadTitle && currentTenant) {
        // Get current title from localStorage to preserve it
        const savedThreads = localStorage.getItem('chatThreads');
        if (savedThreads) {
          const threads = JSON.parse(savedThreads);
          const currentThread = threads.find((t: any) => t.id === threadId);
          const currentTitle = currentThread?.title || 'New Chat';

          onUpdateThreadTitle(threadId, currentTitle, {
            tenantId: currentTenant.id,
            tenantName: currentTenant.name,
            projectUuid: projectUuid || undefined,
            projectName: current === 'All Projects' ? undefined : current,
          });
        }
      }
    };

    window.addEventListener('nomi:tenant-changed', handleTenantChange as EventListener);
    window.addEventListener('nomi:project-changed', handleProjectChange as EventListener);

    return () => {
      window.removeEventListener('nomi:tenant-changed', handleTenantChange as EventListener);
      window.removeEventListener('nomi:project-changed', handleProjectChange as EventListener);
    };
  }, [threadId, onUpdateThreadTitle, currentTenant]);

  // Use a ref to keep track of the complete response outside of React's state system
  const completeResponseRef = useRef("");
  // Track if we've already shown the chart/table/failure report rendering messages
  const chartRenderingShownRef = useRef(false);
  const tableRenderingShownRef = useRef(false);
  const failureReportRenderingShownRef = useRef(false);
  // Add ref for the MessageInput component to manage focus
  const messageInputRef = useRef<{ focusInput: () => void }>(null);
  // Track the event_id for feedback tracking
  const currentEventIdRef = useRef<number | null>(null);
  // Track message count for periodic title regeneration (regenerate every 5 exchanges)
  const messageCountSinceLastTitleRef = useRef<number>(0);
  const TITLE_REGENERATION_INTERVAL = 5; // Regenerate title every N user messages

  // Reset messages only when switching to a different existing thread
  // Don't reset when threadId changes from empty to new (new thread creation)
  const previousThreadId = useRef<string>("");

  useEffect(() => {
    // Case 1: Starting fresh (new chat button clicked) - clear everything
    if (!threadId) {
      setMessages([]);
      setStreamedContent("");
      setWaitingForHumanAssistance(false);
      completeResponseRef.current = "";
      messageCountSinceLastTitleRef.current = 0;
      previousThreadId.current = "";
      return;
    }

    // Case 2: Thread ID exists - always try to load messages from localStorage
    // This handles: page reload, login, AND thread switching
    const threadKey = `thread_messages_${threadId}`;
    const savedMessages = localStorage.getItem(threadKey);

    if (savedMessages) {
      try {
        const parsedMessages = JSON.parse(savedMessages);
        const deserializedMessages: Message[] = parsedMessages.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));

        // Only update if messages are actually different (prevents infinite loops)
        // Compare by message IDs to avoid timestamp comparison issues
        const currentMessageIds = messages.map(m => m.id).join(',');
        const newMessageIds = deserializedMessages.map(m => m.id).join(',');

        if (currentMessageIds !== newMessageIds) {
          setMessages(deserializedMessages);
        }
      } catch (error) {
        console.error('Failed to load messages from localStorage:', error);
        // Only clear if we're actually switching threads
        if (previousThreadId.current !== threadId) {
          setMessages([]);
        }
      }
    } else if (previousThreadId.current !== threadId && previousThreadId.current !== '') {
      // Only clear messages when SWITCHING between existing threads (both have IDs)
      // Do NOT clear when creating a new thread (previous was empty string)
      // This preserves messages when a new thread is being created (user just sent first message)
      setMessages([]);
    } else if (previousThreadId.current === '' && threadId) {
      // New thread creation: previous was empty, now we have an ID
      // Preserve in-memory messages - don't clear them!
    }

    // Reset UI state only on actual thread change
    if (previousThreadId.current !== threadId) {
      setStreamedContent("");
      setWaitingForHumanAssistance(false);
      completeResponseRef.current = "";
      messageCountSinceLastTitleRef.current = 0;
    }

    previousThreadId.current = threadId;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 'messages' intentionally excluded to prevent infinite loops
  }, [threadId]);

  // Save messages to localStorage when they change OR when threadId is set
  useEffect(() => {
    if (threadId && messages.length > 0) {
      const threadKey = `thread_messages_${threadId}`;
      try {
        // Serialize messages with timestamp as ISO string
        const serializedMessages = messages.map(msg => ({
          ...msg,
          timestamp: msg.timestamp.toISOString()
        }));
        localStorage.setItem(threadKey, JSON.stringify(serializedMessages));
      } catch (error) {
        console.error('Failed to save messages to localStorage:', error);
      }
    }
  }, [messages, threadId]);

  // Check if the last message is asking for human assistance
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === "assistant") {
        // Check if the message appears to be a human assistance request
        const content = lastMessage.content;
        if (content) {
          // Look for variations of human assistance requests
          const needsHumanAssistance =
            content.toLowerCase().includes("human assistance") ||
            content.toLowerCase().includes("request human assistance") ||
            content.includes("clarify what to analyse") ||
            content.includes("clarify what to analyze");

          if (needsHumanAssistance) {
            setWaitingForHumanAssistance(true);
            setHumanAssistanceQuery(content);
          }
        }
      }
    }
  }, [messages]);

  // Send message and stream response
  const sendMessage = async (content: string) => {
    if (!content.trim()) return;

    // Clear human assistance state when sending a new message
    setWaitingForHumanAssistance(false);
    setHumanAssistanceQuery("");

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      content,
      role: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);

    // Start streaming
    setIsStreaming(true);
    setStreamedContent("");
    completeResponseRef.current = "";

    // Keep focus on input while waiting for response
    setTimeout(() => {
      messageInputRef.current?.focusInput();
    }, 50); // Very short delay to let state update

    // Construct URL with query parameters
    // Use relative URL in production (empty VITE_API_URL), absolute URL in dev
    const apiUrl = import.meta.env.VITE_API_URL || "";
    const baseUrl = apiUrl || window.location.origin;
    const url = new URL(`${baseUrl}/chat/stream`, window.location.origin);
    url.searchParams.append("message", content);
    if (threadId) {
      url.searchParams.append("thread_id", threadId);
    }
    // Send tenant ID to backend (Phase 5 Task 8)
    if (currentTenant?.id) {
      url.searchParams.append("default_tenant_id", currentTenant.id);
    }

    // Send selected project UUID and name to backend (Project Selector)
    if (currentProject?.uuid) {
      url.searchParams.append("selected_project_uuid", currentProject.uuid);
    }
    if (currentProject?.alias) {
      url.searchParams.append("selected_project_name", currentProject.alias);
    }

    // Add authentication token to URL for EventSource
    const authService = AuthService.getInstance();
    const token = authService.getToken();
    if (token) {
      url.searchParams.append("token", token);
    }

    try {
      // Reset chart, table, and failure report rendering flags for new message
      chartRenderingShownRef.current = false;
      tableRenderingShownRef.current = false;
      failureReportRenderingShownRef.current = false;

      // Create EventSource for SSE
      const eventSource = new EventSource(url.toString());
      eventSourceRef.current = eventSource; // Store for cancellation

      // Handle thread ID
      eventSource.addEventListener("thread_id", async (event) => {
        const newThreadId = event.data;
        if (!threadId) {
          onThreadCreated(newThreadId);

          // Generate title for new thread based on first message
          const generateFallbackTitle = (message: string): string => {
            // Clean and truncate message for title
            const cleaned = message.trim().replace(/\s+/g, ' ');
            if (cleaned.length <= 30) return cleaned;
            // Cut at word boundary
            const truncated = cleaned.substring(0, 30);
            const lastSpace = truncated.lastIndexOf(' ');
            return (lastSpace > 15 ? truncated.substring(0, lastSpace) : truncated) + '...';
          };

          try {
            const titleResponse = await fetch(`${baseUrl}/api/generate-thread-title`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ message: content, threadId: newThreadId }),
            });

            // Capture current tenant/project context for the thread
            const threadContext: ThreadContext = {
              tenantId: currentTenant?.id,
              tenantName: currentTenant?.name,
              projectUuid: currentProject?.uuid,
              projectName: currentProject?.alias,
            };

            if (titleResponse.ok) {
              const data = await titleResponse.json();
              const title = data.title;
              if (title && onUpdateThreadTitle) {
                onUpdateThreadTitle(newThreadId, title, threadContext);
              } else {
                // API returned empty title, use fallback
                const fallback = generateFallbackTitle(content);
                onUpdateThreadTitle?.(newThreadId, fallback, threadContext);
              }
            } else {
              // API call failed, use fallback
              const fallback = generateFallbackTitle(content);
              onUpdateThreadTitle?.(newThreadId, fallback, threadContext);
            }
          } catch (error) {
            console.error('🔴 [TITLE DEBUG ERROR] Failed to generate thread title:', error);
            // Use fallback on error - still save context
            const fallback = generateFallbackTitle(content);
            const threadContext: ThreadContext = {
              tenantId: currentTenant?.id,
              tenantName: currentTenant?.name,
              projectUuid: currentProject?.uuid,
              projectName: currentProject?.alias,
            };
            onUpdateThreadTitle?.(newThreadId, fallback, threadContext);
          }
        }
      });

      // Handle typing indicators
      eventSource.addEventListener("typing", (event) => {
        const typingMessage = event.data;

        // Show typing indicator in the UI with animated dots
        setStreamedContent((prev) => {
          // Remove any existing typing indicators first, but preserve progress lines
          const cleanedPrev = prev
            .split("\n")
            .filter(
              (line) =>
                // ALWAYS preserve progress lines (execution monitoring)
                line.includes("📊") ||
                line.includes("Progress:") ||
                line.startsWith("✅") ||
                line.startsWith("❌") ||
                // Filter out typing indicators (tool execution status)
                (!line.includes("...") &&
                !line.includes("Fetching") &&
                !line.includes("Loading") &&
                !line.includes("Retrieving") &&
                !line.includes("Working") &&
                !line.includes("Running") &&
                !line.includes("Analyzing") &&
                !line.includes("Building") &&
                !line.includes("Transforming") &&
                !line.includes("Accessing") &&
                !line.includes("Checking") &&
                !line.includes("Creating") &&
                !line.includes("Executing")),
            )
            .join("\n");

          return (
            cleanedPrev + (cleanedPrev ? "\n\n" : "") + `*${typingMessage}*`
          );
        });
      });

      // Handle event_id for feedback tracking
      eventSource.addEventListener("event_id", (event) => {
        try {
          const data = JSON.parse(event.data);
          currentEventIdRef.current = data.event_id;
        } catch (e) {
          console.error("Failed to parse event_id:", e);
        }
      });

      // Phase 4: Handle tool_call events for transparency
      eventSource.addEventListener("tool_call", (event) => {
        try {
          const data = JSON.parse(event.data);

          // Skip tool calls with empty or missing names
          const toolName = data.tool_name || "";
          if (!toolName.trim()) {
            console.warn("Skipping tool_call event with empty tool_name:", data);
            return;
          }

          const toolCall: ToolCall = {
            id: `tool-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            tool_name: toolName,
            parameters: data.parameters || {},
            is_critical: data.is_critical || false,
            status: 'executing',
            timestamp: new Date(),
          };

          setActiveToolCalls(prev => [...prev, toolCall]);

          // Mark tool as completed after a delay (or when next tool starts)
          setTimeout(() => {
            setActiveToolCalls(prev =>
              prev.map(t => t.id === toolCall.id ? { ...t, status: 'completed' as const } : t)
            );
          }, 2000);
        } catch (e) {
          console.error("Failed to parse tool_call event:", e);
        }
      });

      // Phase 4: Handle confirmation_required events for critical operations
      eventSource.addEventListener("confirmation_required", (event) => {
        try {
          const data = JSON.parse(event.data);

          const confirmationRequest: ConfirmationRequest = {
            id: data.confirmation_id || `confirm-${Date.now()}`,
            operation: data.operation || data.tool_name,
            tool_name: data.tool_name,
            risk_level: data.risk_level || 'high',
            description: data.description || `This operation requires your confirmation before proceeding.`,
            parameters: data.parameters || {},
            timestamp: new Date(),
          };

          setPendingConfirmation(confirmationRequest);
        } catch (e) {
          console.error("Failed to parse confirmation_required event:", e);
        }
      });

      // Handle graceful error messages from backend (recursion limit, context length, etc.)
      eventSource.addEventListener("error", (event: Event) => {
        // This is a CUSTOM error event from the backend (not SSE connection error)
        // Contains user-friendly error messages like "task too complex" or "conversation too long"
        const messageEvent = event as MessageEvent;
        const errorMessage = messageEvent.data;

        // Append error message to streamed content
        setStreamedContent((prev) => {
          const separator = prev ? "\n\n" : "";
          return prev + separator + "⚠️ " + errorMessage;
        });
        completeResponseRef.current += (completeResponseRef.current ? "\n\n" : "") + "⚠️ " + errorMessage;
      });

      // Handle token streaming
      eventSource.addEventListener("token", (event) => {
        const token = event.data;

        // No classification filtering needed - it's handled server-side with pre-classification

        // Check if we're entering or inside chart or table data section
        const currentContent = completeResponseRef.current + token;

        // Always accumulate tokens first
        completeResponseRef.current += token;

        // If we encounter CHART_DATA_START, start filtering and show rendering message
        if (
          currentContent.includes("CHART_DATA_START") &&
          !chartRenderingShownRef.current
        ) {
          // Show a rendering message instead of raw chart data (only once)
          setStreamedContent((prev) => prev + "\n\n🔄 Rendering chart...");
          chartRenderingShownRef.current = true;
          return; // Don't show this token or any subsequent chart tokens
        }

        // If we encounter TABLE_DATA_START, start filtering and show rendering message
        if (
          currentContent.includes("TABLE_DATA_START") &&
          !tableRenderingShownRef.current
        ) {
          // Show a rendering message instead of raw table data (only once)
          setStreamedContent((prev) => prev + "\n\n📊 Formatting table...");
          tableRenderingShownRef.current = true;
          return; // Don't show this token or any subsequent table tokens
        }

        // If we encounter FAILURE_REPORT_START, start filtering and show rendering message
        if (
          currentContent.includes("FAILURE_REPORT_START") &&
          !failureReportRenderingShownRef.current
        ) {
          // Show a rendering message instead of raw JSON (only once)
          setStreamedContent((prev) => prev + "\n\n📊 Loading failure report...");
          failureReportRenderingShownRef.current = true;
          return; // Don't show this token or any subsequent failure report tokens
        }

        // If we're inside chart data (between START and END), don't show tokens
        if (
          completeResponseRef.current.includes("CHART_DATA_START") &&
          !completeResponseRef.current.includes("CHART_DATA_END")
        ) {
          return;
        }

        // If we're inside table data (between START and END), don't show tokens
        if (
          completeResponseRef.current.includes("TABLE_DATA_START") &&
          !completeResponseRef.current.includes("TABLE_DATA_END")
        ) {
          return;
        }

        // If we're inside failure report data (between START and END), don't show tokens
        if (
          completeResponseRef.current.includes("FAILURE_REPORT_START") &&
          !completeResponseRef.current.includes("FAILURE_REPORT_END")
        ) {
          return;
        }

        // If we see CHART_DATA_END, we're done with chart data - don't show this token either
        if (currentContent.includes("CHART_DATA_END")) {
          return;
        }

        // If we see TABLE_DATA_END, we're done with table data - don't show this token either
        if (currentContent.includes("TABLE_DATA_END")) {
          return;
        }

        // If we see FAILURE_REPORT_END, we're done with failure report - don't show this token either
        if (currentContent.includes("FAILURE_REPORT_END")) {
          return;
        }

        // JSON parsing is no longer needed since classification is handled server-side

        // No need to clean up classification JSON anymore since it's handled server-side

        // Clear any typing indicators when real content starts
        setStreamedContent(completeResponseRef.current);
      });

      // Handle completion
      eventSource.addEventListener("done", () => {
        eventSource.close();
        eventSourceRef.current = null; // Clear ref after closing

        // Phase 4: Clear tool calls after short delay (let user see completed state)
        setTimeout(() => {
          setActiveToolCalls([]);
        }, 3000);

        // Add assistant message to the list if there's content
        if (completeResponseRef.current) {
          // Try to detect and extract chart, table, and failure report data
          let chartData: any = null;
          let tableData: any = null;
          let failureReportData: any = null;
          let cleanContent = completeResponseRef.current;

          try {
            // Look for chart data patterns in the response

            // Handle multiple tables in the response
            // const tableMatches = []; // TODO: Implement multiple table handling
            const hasChartStart = cleanContent.includes("CHART_DATA_START");
            const hasChartEnd = cleanContent.includes("CHART_DATA_END");

            if (hasChartStart && hasChartEnd) {
              // Try a different approach: use regex to extract the JSON structure
              // Look for the pattern between CHART_DATA_START and CHART_DATA_END
              const chartDataMatch = cleanContent.match(/CHART_DATA_START([\s\S]*?)CHART_DATA_END/);

              if (chartDataMatch) {
                const rawContent = chartDataMatch[1];

                // Since the content is coming as a single token, we don't need to clean SSE formatting
                // Just trim the content
                const cleanedContent = rawContent.trim();

                // Try to find a valid JSON object in the cleaned content
                const jsonMatch = cleanedContent.match(/\{[\s\S]*\}/);

                if (jsonMatch) {
                  const jsonContent = jsonMatch[0];

                  try {
                    chartData = JSON.parse(jsonContent);

                    // Remove chart data from content
                    cleanContent = cleanContent
                      .replace(/CHART_DATA_START[\s\S]*?CHART_DATA_END/, "")
                      .trim();
                    // Also remove the rendering message
                    cleanContent = cleanContent
                      .replace(/🔄 Rendering chart\.\.\./, "")
                      .trim();
                  } catch (parseError) {
                    console.error("❌ Failed to parse chart JSON:", parseError);
                    console.error("JSON content that failed:", jsonContent);

                    // Remove chart data markers but keep the raw content visible for debugging
                    cleanContent = cleanContent
                      .replace(/CHART_DATA_START/, "")
                      .replace(/CHART_DATA_END/, "");
                    cleanContent = cleanContent
                      .replace(
                        /🔄 Rendering chart\.\.\./,
                        "❌ Chart rendering failed - invalid data format",
                      )
                      .trim();
                  }
                } else {
                  console.warn("⚠️ No valid JSON structure found in chart data");
                  console.warn("Cleaned content:", cleanedContent);
                }
              } else {
                console.warn("⚠️ No chart data match found in content");
              }
            } else {
              // FALLBACK: Detect raw chart JSON without markers (LLM sometimes strips the markers)

              // Look for JSON objects that look like chart data
              const rawChartMatch = cleanContent.match(/\{[\s\n]*"type"[\s\n]*:[\s\n]*"(bar|timeline|execution_history|line|image|interactive_html)"[\s\n]*,[\s\S]*?"data"[\s\n]*:/);

              if (rawChartMatch) {
                // Find the start of the JSON object
                const jsonStart = cleanContent.indexOf(rawChartMatch[0]);
                if (jsonStart !== -1) {
                  // Count braces to find the complete JSON object
                  let braceCount = 0;
                  let jsonEnd = jsonStart;
                  let started = false;

                  for (let i = jsonStart; i < cleanContent.length; i++) {
                    if (cleanContent[i] === '{') {
                      braceCount++;
                      started = true;
                    }
                    if (cleanContent[i] === '}') {
                      braceCount--;
                    }
                    if (started && braceCount === 0) {
                      jsonEnd = i + 1;
                      break;
                    }
                  }

                  if (jsonEnd > jsonStart) {
                    const rawJson = cleanContent.substring(jsonStart, jsonEnd);

                    try {
                      const parsedChart = JSON.parse(rawJson);
                      // Validate it looks like chart data
                      if (parsedChart.type && (parsedChart.data || parsedChart.daily_execution_counts)) {
                        chartData = parsedChart;

                        // Remove the raw JSON from content
                        cleanContent = cleanContent.substring(0, jsonStart) + cleanContent.substring(jsonEnd);
                        cleanContent = cleanContent.trim();
                      }
                    } catch {
                      // Failed to parse raw chart JSON
                    }
                  }
                }
              }
            }

            // Look for table data patterns in the response

            // Check if TABLE_DATA markers are inside code blocks
            const codeBlockWithTable = cleanContent.match(/```[\s\S]*?(TABLE_DATA_START[\s\S]*?TABLE_DATA_END)[\s\S]*?```/);
            if (codeBlockWithTable) {
              // Extract the content from inside the code block
              const tableSection = codeBlockWithTable[1];
              // Replace the entire code block with just the table data section
              cleanContent = cleanContent.replace(codeBlockWithTable[0], tableSection);
            }

            // First check for HTML-encoded markers
            let hasTableStart = cleanContent.includes("TABLE_DATA_START") ||
                               cleanContent.includes("&lt;TABLE_DATA_START&gt;");
            let hasTableEnd = cleanContent.includes("TABLE_DATA_END") ||
                             cleanContent.includes("&lt;TABLE_DATA_END&gt;");

            // If we find HTML-encoded markers, decode the content first
            if (cleanContent.includes("&lt;") || cleanContent.includes("&gt;")) {
              // Create a temporary div to decode HTML entities
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = cleanContent;
              cleanContent = tempDiv.textContent || cleanContent;

              // Re-check for markers after decoding
              hasTableStart = cleanContent.includes("TABLE_DATA_START");
              hasTableEnd = cleanContent.includes("TABLE_DATA_END");
            }

            if (hasTableStart && hasTableEnd) {
              // Extract just the JSON part
              const jsonStart =
                cleanContent.indexOf("TABLE_DATA_START") +
                "TABLE_DATA_START".length;
              const jsonEnd = cleanContent.indexOf("TABLE_DATA_END");

              if (jsonStart < jsonEnd) {
                const jsonContent = cleanContent
                  .substring(jsonStart, jsonEnd)
                  .trim();

                try {
                  // Clean up the JSON content first
                  let cleanedJson = jsonContent;

                  // Remove any leading/trailing whitespace or newlines
                  cleanedJson = cleanedJson.trim();

                  // Remove any markdown formatting that might be present
                  cleanedJson = cleanedJson.replace(/^```json\s*/i, '').replace(/```\s*$/, '');
                  cleanedJson = cleanedJson.replace(/^```\s*/i, '').replace(/```\s*$/, '');

                  // Try direct JSON parsing first
                  tableData = JSON.parse(cleanedJson);

                  // Validate table data structure
                  if (!tableData.columns || !Array.isArray(tableData.columns)) {
                    console.warn("Invalid table data structure: missing columns");
                    tableData = null;
                  } else if (!tableData.rows || !Array.isArray(tableData.rows)) {
                    console.warn("Invalid table data structure: missing rows");
                    tableData = null;
                  } else {
                    // Transform array-of-arrays to array-of-objects if needed
                    // Backend sends: rows = [["val1", "val2"], ["val3", "val4"]]
                    // TableRenderer expects: rows = [{col1: "val1", col2: "val2"}, {col1: "val3", col2: "val4"}]
                    if (tableData.rows.length > 0 && Array.isArray(tableData.rows[0])) {
                      tableData.rows = tableData.rows.map((row: any[]) => {
                        const rowObj: Record<string, any> = {};
                        tableData.columns.forEach((col: string, index: number) => {
                          rowObj[col] = row[index];
                        });
                        return rowObj;
                      });
                    }
                  }
                } catch (directParseError) {
                  console.error("Parse error:", directParseError);

                  // Try to find a valid JSON object in the content
                  const jsonMatch = jsonContent.match(/\{[\s\S]*\}/);
                  if (jsonMatch) {
                    try {
                      tableData = JSON.parse(jsonMatch[0]);

                      // Transform array-of-arrays to array-of-objects if needed
                      if (tableData.columns && tableData.rows && tableData.rows.length > 0 && Array.isArray(tableData.rows[0])) {
                        tableData.rows = tableData.rows.map((row: any[]) => {
                          const rowObj: Record<string, any> = {};
                          tableData.columns.forEach((col: string, index: number) => {
                            rowObj[col] = row[index];
                          });
                          return rowObj;
                        });
                      }
                    } catch (regexParseError) {
                      console.error("Regex parse also failed:", regexParseError);
                    }
                  } else {
                    console.warn("No JSON structure found in table content");
                  }
                }

                // Remove table data from content (handle both normal and HTML-encoded versions)
                cleanContent = cleanContent
                  .replace(/TABLE_DATA_START[\s\S]*?TABLE_DATA_END/g, "")
                  .replace(/&lt;TABLE_DATA_START&gt;[\s\S]*?&lt;TABLE_DATA_END&gt;/g, "")
                  .trim();
                // Also remove the table formatting message
                cleanContent = cleanContent
                  .replace(/📊 Formatting table\.\.\./g, "")
                  .trim();

                // Clean up any remaining table-related artifacts
                cleanContent = cleanContent
                  .replace(/```[\s\S]*?TABLE_DATA_START[\s\S]*?TABLE_DATA_END[\s\S]*?```/g, "")
                  .trim();

                // Additional cleanup for any stray table markers
                cleanContent = cleanContent
                  .replace(/TABLE_DATA_START/g, "")
                  .replace(/TABLE_DATA_END/g, "")
                  .trim();
              }
            }

            // Look for failure report data patterns in the response
            const hasFailureReportStart = cleanContent.includes("FAILURE_REPORT_START");
            const hasFailureReportEnd = cleanContent.includes("FAILURE_REPORT_END");

            if (hasFailureReportStart && hasFailureReportEnd) {
              // Extract just the JSON part
              const jsonStart =
                cleanContent.indexOf("FAILURE_REPORT_START") +
                "FAILURE_REPORT_START".length;
              const jsonEnd = cleanContent.indexOf("FAILURE_REPORT_END");

              if (jsonStart < jsonEnd) {
                const jsonContent = cleanContent
                  .substring(jsonStart, jsonEnd)
                  .trim();

                try {
                  // Clean up the JSON content first
                  let cleanedJson = jsonContent.trim();

                  // Remove any markdown formatting that might be present
                  cleanedJson = cleanedJson.replace(/^```json\s*/i, '').replace(/```\s*$/, '');
                  cleanedJson = cleanedJson.replace(/^```\s*/i, '').replace(/```\s*$/, '');

                  // Try direct JSON parsing
                  failureReportData = JSON.parse(cleanedJson);

                  // Validate failure report data structure
                  if (failureReportData.type !== "failure_report") {
                    console.warn("Invalid failure report data structure: wrong type");
                    failureReportData = null;
                  }
                } catch (directParseError) {
                  console.error("Parse error:", directParseError);

                  // Try to find a valid JSON object in the content
                  const jsonMatch = jsonContent.match(/\{[\s\S]*\}/);
                  if (jsonMatch) {
                    try {
                      failureReportData = JSON.parse(jsonMatch[0]);
                    } catch (regexParseError) {
                      console.error("Regex parse also failed:", regexParseError);
                    }
                  } else {
                    console.warn("No JSON structure found in failure report content");
                  }
                }

                // Remove failure report data from content
                cleanContent = cleanContent
                  .replace(/FAILURE_REPORT_START[\s\S]*?FAILURE_REPORT_END/g, "")
                  .trim();
                // Also remove the loading message
                cleanContent = cleanContent
                  .replace(/📊 Loading failure report\.\.\./g, "")
                  .trim();

                // Additional cleanup for any stray failure report markers
                cleanContent = cleanContent
                  .replace(/FAILURE_REPORT_START/g, "")
                  .replace(/FAILURE_REPORT_END/g, "")
                  .trim();
              }
            }
          } catch (e) {
            console.error("Failed to parse chart/table data:", e);
          }

          const assistantMessage: Message = {
            id: Date.now().toString(),
            content: cleanContent,
            role: "assistant",
            timestamp: new Date(),
            chartData: chartData,
            tableData: tableData,
            failureReportData: failureReportData,
            eventId: currentEventIdRef.current ?? undefined,
          };

          // Add the message to the list WITHOUT replacing previous messages
          setMessages((prev) => [...prev, assistantMessage]);

          // Reset event_id for next message
          currentEventIdRef.current = null;
          setStreamedContent("");
          completeResponseRef.current = "";
        }

        setIsStreaming(false);

        // Track message count for periodic title regeneration
        messageCountSinceLastTitleRef.current += 1;

        // Periodically regenerate title based on conversation progress
        if (threadId && onUpdateThreadTitle && messageCountSinceLastTitleRef.current >= TITLE_REGENERATION_INTERVAL) {
          messageCountSinceLastTitleRef.current = 0; // Reset counter

          // Get recent messages for title context (last 3 exchanges = 6 messages)
          const recentMessages = messages.slice(-6).map(m => `${m.role}: ${m.content.substring(0, 100)}`).join('\n');

          // Generate new title based on conversation progress
          (async () => {
            try {
              const titleResponse = await fetch(`${baseUrl}/api/generate-thread-title`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  message: recentMessages,
                  threadId: threadId,
                  isUpdate: true // Flag to indicate this is a title update, not initial creation
                }),
              });

              if (titleResponse.ok) {
                const data = await titleResponse.json();
                if (data.title) {
                  // Preserve current context when updating title
                  const savedThreads = localStorage.getItem('chatThreads');
                  if (savedThreads) {
                    const threads = JSON.parse(savedThreads);
                    const currentThread = threads.find((t: any) => t.id === threadId);
                    onUpdateThreadTitle(threadId, data.title, {
                      tenantId: currentThread?.tenantId || currentTenant?.id,
                      tenantName: currentThread?.tenantName || currentTenant?.name,
                      projectUuid: currentThread?.projectUuid || currentProject?.uuid,
                      projectName: currentThread?.projectName || currentProject?.alias,
                    });
                  }
                }
              }
            } catch (error) {
              console.error('Failed to regenerate title:', error);
            }
          })();
        }

        // Restore focus to input after response is complete
        setTimeout(() => {
          messageInputRef.current?.focusInput();
        }, 100); // Small delay to ensure UI updates are complete
      });

      // Handle errors
      eventSource.onerror = (error) => {
        console.error("EventSource error:", error);
        eventSource.close();
        eventSourceRef.current = null; // Clear ref after closing

        // If there was partial content, save it
        if (completeResponseRef.current) {
          const assistantMessage: Message = {
            id: Date.now().toString(),
            content: completeResponseRef.current + " [Connection interrupted]",
            role: "assistant",
            timestamp: new Date(),
          };

          setMessages((prev) => [...prev, assistantMessage]);
          setStreamedContent("");
          completeResponseRef.current = "";
        }

        setIsStreaming(false);

        // Restore focus to input after error
        setTimeout(() => {
          messageInputRef.current?.focusInput();
        }, 100);
      };
    } catch (error) {
      console.error("Error connecting to stream:", error);
      setIsStreaming(false);

      // Restore focus to input after catch error
      setTimeout(() => {
        messageInputRef.current?.focusInput();
      }, 100);
    }
  };

  // Cancel active stream
  const cancelStream = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;

      // Save any partial content that was streamed
      if (completeResponseRef.current) {
        const partialMessage: Message = {
          id: Date.now().toString(),
          content: completeResponseRef.current + "\n\n*[Response stopped by user]*",
          role: "assistant",
          timestamp: new Date(),
          eventId: currentEventIdRef.current || undefined,
        };
        setMessages(prev => [...prev, partialMessage]);
      }

      // Re-enable input
      setIsStreaming(false);
      setStreamedContent("");
      completeResponseRef.current = "";

      // Focus input
      setTimeout(() => {
        messageInputRef.current?.focusInput();
      }, 50);
    }
  };

  // Handle human assistance response
  const handleHumanAssistanceResponse = async (response: string) => {
    if (!response.trim() || !threadId) return;

    try {
      const baseUrl =
        (import.meta as any).env.VITE_API_URL || "";
      const resumeResponse = await fetch(`${baseUrl}/chat/resume`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          human_response: response,
          thread_id: threadId,
        }),
      });

      if (resumeResponse.ok) {
        const result = await resumeResponse.json();

        // Add human response message
        const humanMessage: Message = {
          id: Date.now().toString(),
          content: response,
          role: "user",
          timestamp: new Date(),
        };

        // Add assistant response
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          content: result.response,
          role: "assistant",
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, humanMessage, assistantMessage]);
        setWaitingForHumanAssistance(false);
        setHumanAssistanceQuery("");

        // Restore focus to input after human assistance response
        setTimeout(() => {
          messageInputRef.current?.focusInput();
        }, 100);
      }
    } catch (error) {
      console.error("Error sending human assistance response:", error);

      // Restore focus to input after error
      setTimeout(() => {
        messageInputRef.current?.focusInput();
      }, 100);
    }
  };

  // Phase 4: Handle confirmation response
  // When user clicks Confirm on the modal, store the response on backend
  // and auto-send "yes" as a chat message so the agent processes it cleanly.
  const handleConfirmation = useCallback(async (requestId: string) => {
    if (!pendingConfirmation || pendingConfirmation.id !== requestId) return;

    // Store confirmation on backend so check_confirmation_response finds it
    try {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      await fetch(`${baseUrl}/chat/confirm-operation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          confirmation_id: requestId,
          confirmed: true,
          thread_id: threadId,
        }),
      });
    } catch (error) {
      console.error('Error storing confirmation:', error);
    }

    // Clear the modal
    setPendingConfirmation(null);

    // Send "yes" as a chat message so the agent processes the confirmation
    await sendMessage("yes");
  }, [pendingConfirmation, sendMessage, threadId]);

  // Phase 4: Handle confirmation cancellation
  // When user clicks Cancel on the modal, store the cancellation
  // and auto-send "no" so the agent sees it.
  const handleCancelConfirmation = useCallback(async (requestId: string) => {
    if (!pendingConfirmation || pendingConfirmation.id !== requestId) return;

    // Store cancellation on backend
    try {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      await fetch(`${baseUrl}/chat/confirm-operation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          confirmation_id: requestId,
          confirmed: false,
          thread_id: threadId,
        }),
      });
    } catch (error) {
      console.error('Error storing cancellation:', error);
    }

    // Clear the modal
    setPendingConfirmation(null);

    // Send "no" as a chat message so the agent processes the cancellation
    await sendMessage("no");
  }, [pendingConfirmation, sendMessage, threadId]);

  // Handle feedback submission
  const handleFeedback = async (
    eventId: number,
    type: 'positive' | 'negative',
    comment?: string
  ): Promise<void> => {
    try {
      const baseUrl = import.meta.env.VITE_API_URL || "";
      const response = await fetch(`${baseUrl}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          event_id: eventId,
          feedback_type: type,
          comment: comment || null,
        }),
      });

      if (!response.ok) {
        throw new Error('Feedback submission failed');
      }

    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw error; // Re-throw to let the component handle it
    }
  };

  return (
    <div className="chat-container">
      <header className="chat-header">
        <div className="header-brand">
          <img src={`${import.meta.env.BASE_URL}favicon.ico`} alt="Nomi" className="header-logo" />
          <div className="header-brand-text">
            <span className="header-brand-name">NOMI</span>
            <span className="header-brand-tagline">AI-powered data intelligence</span>
          </div>
        </div>
        <div className="header-right">
          {/* Badge removed - multi-tenant system uses tenant selector instead */}
        </div>
      </header>

      <div className="chat-content">
        {/* Show welcome when no conversation started (ignore system messages) */}
        {!messages.some(m => m.role !== 'system') && !isStreaming && (
          <div className="welcome-message">
            <img src={`${import.meta.env.BASE_URL}favicon.ico`} alt="Nomi" className="welcome-logo" />
            <h3>Welcome to Nomi</h3>
            <p>I can help you with:</p>
            <ul className="welcome-capabilities">
              <li><strong>Explore your data</strong> — Browse databases, schemas, and sample data across connections</li>
              <li><strong>Build pipelines</strong> — Create tasks that extract, transform, and load data automatically</li>
              <li><strong>Execute & monitor</strong> — Run SQL queries and track task progress in real-time</li>
              <li><strong>Analyze & visualize</strong> — Generate reports, insights, and data visualizations</li>
              {!isSingleTenant && (
                <li><strong>Manage at scale</strong> — Copy configurations across projects and environments</li>
              )}
            </ul>
            <p className="welcome-prompt">
              What would you like to work on today?
            </p>
          </div>
        )}

        <MessageList
          messages={messages}
          streamedContent={streamedContent}
          isStreaming={isStreaming}
          onFeedback={handleFeedback}
        />

        {/* Phase 4: Tool call transparency indicator - at bottom so it stays visible */}
        {activeToolCalls.length > 0 && (
          <ToolCallIndicator
            toolCalls={activeToolCalls}
            isStreaming={isStreaming}
          />
        )}
      </div>

      {waitingForHumanAssistance && (
        <div className="human-assistance-bar">
          <div className="assistance-info">
            <AlertCircle size={16} />
            <span>Waiting for human assistance</span>
          </div>
          <MessageInput
            ref={messageInputRef}
            onSendMessage={handleHumanAssistanceResponse}
            onCancel={cancelStream}
            disabled={false}
            isStreaming={isStreaming}
            placeholder="Provide additional information..."
          />
        </div>
      )}

      {!waitingForHumanAssistance && (
        <MessageInput
          ref={messageInputRef}
          onSendMessage={sendMessage}
          onCancel={cancelStream}
          disabled={isStreaming}
          isStreaming={isStreaming}
          placeholder={
            isStreaming
              ? "Nomi is thinking..."
              : "Ask Nomi anything about your data..."
          }
        />
      )}

      {/* Phase 4: Confirmation modal for critical operations */}
      {pendingConfirmation && (
        <ConfirmationModal
          request={pendingConfirmation}
          onConfirm={handleConfirmation}
          onCancel={handleCancelConfirmation}
          isOpen={pendingConfirmation !== null}
        />
      )}
    </div>
  );
}

export default ChatContainer;
